import React, { FunctionComponent, useEffect, useState } from 'react'
import { useReservations } from 'components/views/reservations/ReservationsProvider'
import { useTranslation } from 'react-i18next'
import { useMessage } from 'Elements/Message'
import { IReservationsListReservationData, useApi } from 'components/providers/ApiProvider'
import ListSkeleton from 'Elements/ListSkeleton'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import Container from '@material-ui/core/Container'
import Alert from '@material-ui/lab/Alert'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import ListTablePagination from 'Elements/ListTablePagination'
import ListTableRow from 'Elements/ListTableRow'
import { formatDateTimeFieldToISO, formatTimestamp } from 'utils'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const api = useApi()
  const { t } = useTranslation()
  const { showError } = useMessage()

  const { filter, loadList, listLoading, setListLoading, listPerPage, onChangePerPage, listPage, setListPage, onChangePage } = useReservations()

  const [reservations, setReservations] = useState<IReservationsListReservationData[]>(null)
  const [totalReservations, setTotalReservations] = useState(0)

  const load = (onLoad?: Function, page?: number) => {
    if (!page) {
      setListLoading(true)
    }

    api.getReservationsList({
      zone_id: filter.zone || undefined,
      reservation_type_id: filter.reservationType || undefined,
      plate_number: filter.plateNumber.toUpperCase() || undefined,
      client: filter.client || undefined,
      ...(filter.createdFrom && { created_from: formatDateTimeFieldToISO(filter.createdFrom) }),
      ...(filter.createdTill && { created_to: formatDateTimeFieldToISO(filter.createdTill) }),
      limit: listPerPage,
      page: page || listPage,
    })
      .then(data => {
        if (!page && data.reservations && !data.reservations.length && data.totalReservations) {
          const pages = Math.ceil(data.totalReservations / listPerPage)

          if (listPage > pages) {
            setListPage(pages)
            load(onLoad, pages)

            return
          }
        }

        setListLoading(false)

        if (onLoad) {
          onLoad(data.reservations)
        }

        setReservations(data.reservations)
        setTotalReservations(data.totalReservations)
      })
      .catch(error => {
        setListLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    onChangePage(page + 1)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangePerPage(parseInt(event.target.value, 10))
  }

  const nowTime = (new Date).getTime()

  return (
    <>
      {!reservations && (
        <ListSkeleton />
      )}

      {reservations && listLoading && (
        <ListLoadingIndicator />
      )}

      {reservations && !reservations.length && (
        <Container maxWidth="md" style={{ paddingTop: 36 }}>
          <Alert severity="info">{t('noRecords')}</Alert>
        </Container>
      )}

      {reservations && Boolean(reservations.length) && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zone')}</TableCell>
                  <TableCell>{t('reservedAt')}</TableCell>
                  <TableCell>{t('type')}</TableCell>
                  <TableCell>{t('period')}</TableCell>
                  <TableCell>{t('plateNumber')}</TableCell>
                  <TableCell>{t('client')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((reservation, key) => {
                  const closedReservation = new Date(reservation.valid_to).getTime() <= nowTime

                  return <ListTableRow key={key} {...(closedReservation && { fade: true })}>
                    <TableCell>
                      {reservation.zone.code}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(reservation.create_time)}
                    </TableCell>
                    <TableCell>
                      {reservation.reservation_type.name}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(reservation.valid_from)} - {formatTimestamp(reservation.valid_to)}
                    </TableCell>
                    <TableCell style={{ fontFamily: '"Roboto Mono", monospace' }}>
                      {reservation.plate_number}
                    </TableCell>
                    <TableCell>
                      {reservation.clients.join(', ')}
                    </TableCell>
                  </ListTableRow>
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ListTablePagination
            count={totalReservations}
            rowsPerPage={listPerPage}
            page={listPage - 1}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </>
      )}
    </>
  )
}

export default List
